import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { profileApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { usePricing } from '@/hooks/usePricing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ShoppingBag, User, Heart, Loader2, Package, Calendar,
  Store, ChevronRight, Truck, MapPin, Mail, Phone, Lock,
  Trash2, Eye, EyeOff, AlertTriangle, CheckCircle, Clock,
  CreditCard, FileText, X, Building2
} from 'lucide-react';

const formatSEK = (n) => (n || 0).toLocaleString('sv-SE') + ' kr';
const formatDate = (d) => d ? new Date(d).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
const formatDateShort = (d) => d ? new Date(d).toLocaleDateString('sv-SE') : '-';

const StatusBadge = ({ status }) => {
  const map = {
    new: { label: 'Ny', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
    pending: { label: 'Väntande', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    paid: { label: 'Betald', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CreditCard },
    confirmed: { label: 'Bekräftad', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
    shipped: { label: 'Skickad', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Truck },
    delivered: { label: 'Levererad', cls: 'bg-teal-50 text-teal-700 border-teal-200', icon: Package },
    completed: { label: 'Avslutad', cls: 'bg-gray-50 text-gray-700 border-gray-200', icon: CheckCircle },
    cancelled: { label: 'Avbruten', cls: 'bg-red-50 text-red-600 border-red-200', icon: X },
  };
  const c = map[status] || { label: status || 'Okänd', cls: 'bg-gray-50 text-gray-600 border-gray-200', icon: Clock };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${c.cls}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />{c.label}
    </span>
  );
};

/* ═══════════ ORDERHISTORIK ═══════════ */
const OrdersTab = ({ orders, loading, onViewOrder }) => {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#003B73]" /></div>;

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <ShoppingBag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-bold text-[#0F172A] mb-1">Inga ordrar ännu</h3>
        <p className="text-sm text-gray-500 mb-4">Du har inte gjort några köp ännu.</p>
        <Link to="/produkter"><Button className="bg-[#FF8A00] hover:bg-[#e67c00] rounded-xl">Börja handla</Button></Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order) => {
        const storeNames = Object.values(order.stores || {}).map(s => s.name).join(', ') || '-';
        return (
          <div key={order.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow" data-testid={`order-card-${order.id}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-xs font-bold text-[#003B73] bg-blue-50 px-2.5 py-1 rounded-lg" data-testid="order-number">
                    {(order.order_number || order.id?.slice(0, 8)).toUpperCase()}
                  </span>
                  <StatusBadge status={order.status} />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDateShort(order.created_at)}</span>
                  <span className="flex items-center gap-1"><Store className="h-3.5 w-3.5" />{storeNames}</span>
                  <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" />{order.items?.length || 0} produkt{(order.items?.length || 0) !== 1 ? 'er' : ''}</span>
                  <span className="flex items-center gap-1">
                    {order.delivery_method === 'pickup'
                      ? <><MapPin className="h-3.5 w-3.5" /> Hämtas i butik</>
                      : <><Truck className="h-3.5 w-3.5" /> Hemleverans</>
                    }
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-lg font-black text-[#0F172A]">{formatSEK(order.grand_total || order.total)}</p>
                  {(order.shipping_cost > 0) && <p className="text-xs text-gray-400">inkl. frakt {formatSEK(order.shipping_cost)}</p>}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-[#003B73] text-[#003B73] hover:bg-[#003B73] hover:text-white"
                  onClick={() => onViewOrder(order.id)}
                  data-testid={`view-order-${order.id}`}
                >
                  <Eye className="h-4 w-4 mr-1" /> Visa
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════ ORDERDETALJER (Dialog) ═══════════ */
const OrderDetailDialog = ({ orderId, open, onClose, pricing }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId && open) {
      setLoading(true);
      profileApi.getOrderDetail(orderId)
        .then(res => setOrder(res.data))
        .catch(() => toast.error('Kunde inte hämta orderdetaljer'))
        .finally(() => setLoading(false));
    }
  }, [orderId, open]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#003B73]" /></div>
        ) : order ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-[#003B73]" />
                Order {(order.order_number || order.id?.slice(0, 8)).toUpperCase()}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Status & datum */}
              <div className="flex items-center gap-3">
                <StatusBadge status={order.status} />
                <span className="text-sm text-gray-500">{formatDate(order.created_at)}</span>
              </div>

              {/* Produktlista */}
              <div>
                <h4 className="text-sm font-bold text-[#0F172A] mb-3">Produkter</h4>
                <div className="space-y-2">
                  {(order.items || []).map((item, i) => {
                    const storeName = order.stores?.[item.store_id]?.name || '-';
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`order-item-${i}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{item.title}</p>
                          <p className="text-xs text-gray-400">{storeName} &middot; {item.quantity} st</p>
                        </div>
                        <p className="font-semibold text-sm text-gray-800">{formatSEK(item.price * item.quantity)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Prissammanfattning */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                {pricing.isBusiness ? (
                  <>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Delsumma exkl. moms</span><span className="font-medium">{formatSEK(pricing.priceExclVat(order.subtotal))}</span></div>
                    {order.shipping_cost > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Frakt exkl. moms</span><span className="font-medium">{formatSEK(pricing.priceExclVat(order.shipping_cost))}</span></div>}
                    {order.handling_fee > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Expeditionsavgift exkl. moms</span><span className="font-medium">{formatSEK(pricing.priceExclVat(order.handling_fee))}</span></div>}
                    <div className="border-t border-gray-200 pt-2"><div className="flex justify-between text-sm"><span className="text-gray-500 font-medium">Summa exkl. moms</span><span className="font-bold">{formatSEK(pricing.priceExclVat(order.grand_total || order.total))}</span></div></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Moms (25%)</span><span className="font-medium">{formatSEK(pricing.vatAmount(order.grand_total || order.total))}</span></div>
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                      <span>Totalt inkl. moms</span><span className="text-[#003B73]">{formatSEK(order.grand_total || order.total)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Delsumma</span><span className="font-medium">{formatSEK(order.subtotal)}</span></div>
                    {order.shipping_cost > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Frakt</span><span className="font-medium">{formatSEK(order.shipping_cost)}</span></div>}
                    {order.handling_fee > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Expeditionsavgift</span><span className="font-medium">{formatSEK(order.handling_fee)}</span></div>}
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                      <span>Totalt</span><span className="text-[#003B73]">{formatSEK(order.grand_total || order.total)}</span>
                    </div>
                    <p className="text-xs text-gray-400 pt-1">Inkl. moms</p>
                  </>
                )}
              </div>

              {/* Leveransinformation */}
              {order.shipping_address && (
                <div>
                  <h4 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-1.5"><Truck className="h-4 w-4" /> Leveransadress</h4>
                  <p className="text-sm text-gray-600">{order.shipping_address}</p>
                  {order.shipping_code && <p className="text-xs text-gray-400 mt-1">Fraktkod: {order.shipping_code}</p>}
                  {order.carrier && <p className="text-xs text-gray-400">Transportör: {order.carrier}</p>}
                </div>
              )}

              {order.delivery_method === 'pickup' && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl">
                  <MapPin className="h-4 w-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-700">Denna order hämtas i butik.</p>
                </div>
              )}

              {/* Butikskontakt */}
              {Object.keys(order.stores || {}).length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-1.5"><Store className="h-4 w-4" /> Kontakt — butik</h4>
                  <div className="space-y-2">
                    {Object.entries(order.stores).map(([sid, store]) => (
                      <div key={sid} className="p-3 bg-gray-50 rounded-xl text-sm">
                        <p className="font-medium text-gray-900">{store.name}</p>
                        {store.contact_email && <p className="text-gray-500 flex items-center gap-1 mt-1"><Mail className="h-3.5 w-3.5" />{store.contact_email}</p>}
                        {store.contact_phone && <p className="text-gray-500 flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{store.contact_phone}</p>}
                        {store.location && <p className="text-gray-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{store.location}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <DialogClose asChild><Button variant="outline" className="rounded-xl">Stäng</Button></DialogClose>
            </DialogFooter>
          </>
        ) : (
          <p className="text-gray-500 py-8 text-center">Kunde inte ladda orderdetaljer.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ═══════════ KONTOUPPGIFTER ═══════════ */
const AccountTab = ({ onAccountDeleted }) => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', postal_code: '', city: '', account_type: 'private', company_name: '', organization_number: '', invoice_address: '' });

  // Password
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Delete
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    profileApi.getAccount()
      .then(res => {
        setAccount(res.data);
        setForm({
          name: res.data.name || '',
          email: res.data.email || '',
          phone: res.data.phone || '',
          address: res.data.address || '',
          postal_code: res.data.postal_code || '',
          city: res.data.city || '',
          account_type: res.data.account_type || 'private',
          company_name: res.data.company_name || '',
          organization_number: res.data.organization_number || '',
          invoice_address: res.data.invoice_address || '',
        });
      })
      .catch(() => toast.error('Kunde inte hämta kontouppgifter'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {};
      if (form.name !== account.name) data.name = form.name;
      if (form.email !== account.email) data.email = form.email;
      if (form.phone !== (account.phone || '')) data.phone = form.phone;
      if (form.address !== (account.address || '')) data.address = form.address;
      if (form.postal_code !== (account.postal_code || '')) data.postal_code = form.postal_code;
      if (form.city !== (account.city || '')) data.city = form.city;
      if (form.account_type !== (account.account_type || 'private')) data.account_type = form.account_type;
      if (form.company_name !== (account.company_name || '')) data.company_name = form.company_name;
      if (form.organization_number !== (account.organization_number || '')) data.organization_number = form.organization_number;
      if (form.invoice_address !== (account.invoice_address || '')) data.invoice_address = form.invoice_address;

      if (Object.keys(data).length === 0) { toast.info('Inga ändringar att spara'); setSaving(false); return; }

      const res = await profileApi.updateAccount(data);
      setAccount(res.data.user);
      toast.success('Kontouppgifter sparade');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte spara');
    } finally { setSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPw.length < 6) { toast.error('Lösenordet måste vara minst 6 tecken'); return; }
    if (passwordForm.newPw !== passwordForm.confirm) { toast.error('Lösenorden matchar inte'); return; }
    setSavingPw(true);
    try {
      await profileApi.changePassword(passwordForm.current, passwordForm.newPw);
      toast.success('Lösenordet har ändrats');
      setShowPasswordForm(false);
      setPasswordForm({ current: '', newPw: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte ändra lösenord');
    } finally { setSavingPw(false); }
  };

  const handleDeleteAccount = async () => {
    try {
      await profileApi.deleteAccount();
      toast.success('Kontot har raderats');
      onAccountDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte radera kontot');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#003B73]" /></div>;

  return (
    <div className="space-y-6">
      {/* Kontoinformation */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-[#0F172A] mb-5">Kontoinformation</h3>
        <form onSubmit={handleSave} className="space-y-4">
          {/* Kontotyp */}
          <div>
            <Label>Kontotyp</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button type="button" onClick={() => setForm({...form, account_type: 'private'})}
                className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${form.account_type !== 'business' ? 'border-[#003B73] bg-blue-50/50 text-[#003B73]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="profile-account-type-private">
                <User className="h-5 w-5 flex-shrink-0" /><div><p className="font-semibold text-sm">Privatperson</p><p className="text-[11px] opacity-70">Priser inkl. moms</p></div>
              </button>
              <button type="button" onClick={() => setForm({...form, account_type: 'business'})}
                className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${form.account_type === 'business' ? 'border-[#003B73] bg-blue-50/50 text-[#003B73]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="profile-account-type-business">
                <Building2 className="h-5 w-5 flex-shrink-0" /><div><p className="font-semibold text-sm">Företag</p><p className="text-[11px] opacity-70">Priser exkl. moms</p></div>
              </button>
            </div>
          </div>

          {/* Företagsfält */}
          {form.account_type === 'business' && (
            <div className="space-y-4 p-4 bg-blue-50/40 rounded-xl border border-blue-100">
              <div>
                <Label htmlFor="acc-company">Företagsnamn</Label>
                <Input id="acc-company" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="AB Företaget" data-testid="account-company-name" />
              </div>
              <div>
                <Label htmlFor="acc-org">Organisationsnummer</Label>
                <Input id="acc-org" value={form.organization_number} onChange={e => setForm({...form, organization_number: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="556677-8899" data-testid="account-org-number" />
              </div>
              <div>
                <Label htmlFor="acc-invoice-addr">Fakturaadress</Label>
                <Input id="acc-invoice-addr" value={form.invoice_address} onChange={e => setForm({...form, invoice_address: e.target.value})} className="mt-1 rounded-xl bg-white" placeholder="Storgatan 1, 123 45 Stockholm" data-testid="account-invoice-address" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="acc-name">Fullständigt namn</Label>
              <Input id="acc-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-xl" data-testid="account-name" />
            </div>
            <div>
              <Label htmlFor="acc-email">E-postadress</Label>
              <Input id="acc-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 rounded-xl" data-testid="account-email" />
            </div>
            <div>
              <Label htmlFor="acc-phone">Telefonnummer</Label>
              <Input id="acc-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 rounded-xl" placeholder="070-123 45 67" data-testid="account-phone" />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4 mt-4">
            <h4 className="text-sm font-bold text-gray-700 mb-3">Adress</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <Label htmlFor="acc-address">Gatuadress</Label>
                <Input id="acc-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1 rounded-xl" placeholder="Storgatan 1" data-testid="account-address" />
              </div>
              <div>
                <Label htmlFor="acc-postal">Postnummer</Label>
                <Input id="acc-postal" value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} className="mt-1 rounded-xl" placeholder="123 45" data-testid="account-postal" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="acc-city">Ort</Label>
                <Input id="acc-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-1 rounded-xl" placeholder="Stockholm" data-testid="account-city" />
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <p className="text-xs text-gray-400">Kundnummer: {account?.customer_number || '-'}</p>
            <Button type="submit" disabled={saving} className="bg-[#003B73] hover:bg-[#002a52] rounded-xl" data-testid="save-account-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Spara ändringar
            </Button>
          </div>
        </form>
      </div>

      {/* Lösenord */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><Lock className="h-5 w-5" /> Lösenord</h3>
          {!showPasswordForm && (
            <Button variant="outline" size="sm" onClick={() => setShowPasswordForm(true)} className="rounded-xl" data-testid="change-password-toggle">
              Ändra lösenord
            </Button>
          )}
        </div>
        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label>Nuvarande lösenord</Label>
              <div className="relative mt-1">
                <Input type={showPw ? 'text' : 'password'} value={passwordForm.current} onChange={e => setPasswordForm({...passwordForm, current: e.target.value})} required className="pr-10 rounded-xl" data-testid="current-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nytt lösenord</Label>
                <Input type="password" value={passwordForm.newPw} onChange={e => setPasswordForm({...passwordForm, newPw: e.target.value})} required minLength={6} className="mt-1 rounded-xl" data-testid="new-password" />
              </div>
              <div>
                <Label>Bekräfta nytt lösenord</Label>
                <Input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})} required className="mt-1 rounded-xl" data-testid="confirm-new-password" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={savingPw} className="bg-[#003B73] hover:bg-[#002a52] rounded-xl" data-testid="save-password-btn">
                {savingPw ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Spara lösenord
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setShowPasswordForm(false); setPasswordForm({ current: '', newPw: '', confirm: '' }); }} className="rounded-xl">Avbryt</Button>
            </div>
          </form>
        )}
        {!showPasswordForm && <p className="text-sm text-gray-400">Lösenordet ändrades senast {account?.updated_at ? formatDate(account.updated_at) : 'okänt'}.</p>}
      </div>

      {/* Radera konto */}
      <div className="bg-white rounded-2xl border border-red-100 p-6">
        <h3 className="text-lg font-bold text-red-600 flex items-center gap-2 mb-2"><AlertTriangle className="h-5 w-5" /> Radera konto</h3>
        <p className="text-sm text-gray-500 mb-4">
          Om du raderar ditt konto tas alla uppgifter bort permanent. Detta kan inte ångras. (GDPR — rätt till radering)
        </p>
        <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl" onClick={() => setShowDeleteDialog(true)} data-testid="delete-account-btn">
          <Trash2 className="h-4 w-4 mr-2" /> Radera mitt konto
        </Button>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Radera konto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Skriv <strong>RADERA</strong> nedan för att bekräfta att du vill ta bort ditt konto och alla tillhörande uppgifter.
              </p>
              <Input
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="Skriv RADERA"
                className="rounded-xl"
                data-testid="delete-confirm-input"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild><Button variant="outline" className="rounded-xl">Avbryt</Button></DialogClose>
              <Button
                disabled={deleteConfirm !== 'RADERA'}
                onClick={handleDeleteAccount}
                className="bg-red-600 hover:bg-red-700 rounded-xl"
                data-testid="confirm-delete-btn"
              >
                Radera permanent
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

/* ═══════════ FAVORITER ═══════════ */
const FavoritesTab = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await profileApi.getFavorites();
      setFavorites(res.data.favorites || []);
    } catch { /* empty list on error */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  const removeFavorite = async (productId) => {
    try {
      await profileApi.removeFavorite(productId);
      setFavorites(prev => prev.filter(p => p.id !== productId));
      toast.success('Favorit borttagen');
    } catch { toast.error('Kunde inte ta bort favorit'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#003B73]" /></div>;

  if (favorites.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-bold text-[#0F172A] mb-1">Inga favoriter ännu</h3>
        <p className="text-sm text-gray-500 mb-4">Spara produkter du gillar genom att klicka på hjärtat.</p>
        <Link to="/produkter"><Button className="bg-[#FF8A00] hover:bg-[#e67c00] rounded-xl">Utforska produkter</Button></Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {favorites.map((product) => (
        <div key={product.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow" data-testid={`favorite-card-${product.id}`}>
          <Link to={`/produkt/${product.id}`}>
            <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
              {product.images?.[0]
                ? <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><Package className="h-8 w-8 text-gray-300" /></div>
              }
            </div>
          </Link>
          <div className="p-4">
            <Link to={`/produkt/${product.id}`}>
              <h4 className="font-semibold text-[#0F172A] text-sm truncate hover:text-[#003B73]">{product.title}</h4>
            </Link>
            <p className="text-xs text-gray-400 mt-0.5">{product.store_name}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-lg font-black text-[#FF8A00]">{formatSEK(product.price)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                onClick={() => removeFavorite(product.id)}
                data-testid={`remove-favorite-${product.id}`}
              >
                <Heart className="h-4 w-4 fill-current" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ═══════════ PROFILSIDA ═══════════ */
export const ProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, logout } = useAuth();
  const pricing = usePricing();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [viewOrderId, setViewOrderId] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error('Du måste vara inloggad');
      navigate('/logga-in');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      setOrdersLoading(true);
      profileApi.getOrders()
        .then(res => setOrders(res.data.orders || []))
        .catch(() => {})
        .finally(() => setOrdersLoading(false));
    }
  }, [isAuthenticated]);

  const handleAccountDeleted = () => {
    logout();
    navigate('/');
  };

  if (authLoading) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73]" /></div>;

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="profile-page">
      <div className="container-main py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-black text-[#0F172A]">Min profil</h1>
          <p className="text-gray-500 mt-1">Hantera dina ordrar, kontouppgifter och favoriter.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1 shadow-sm mb-8" data-testid="profile-tabs">
            <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-orders">
              <ShoppingBag className="h-4 w-4 mr-2" /> Orderhistorik
              {orders.length > 0 && <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded text-xs font-bold">{orders.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="account" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-account">
              <User className="h-4 w-4 mr-2" /> Kontouppgifter
            </TabsTrigger>
            <TabsTrigger value="favorites" className="rounded-lg data-[state=active]:bg-[#003B73] data-[state=active]:text-white" data-testid="tab-favorites">
              <Heart className="h-4 w-4 mr-2" /> Favoriter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <OrdersTab orders={orders} loading={ordersLoading} onViewOrder={(id) => setViewOrderId(id)} />
          </TabsContent>

          <TabsContent value="account">
            <AccountTab onAccountDeleted={handleAccountDeleted} />
          </TabsContent>

          <TabsContent value="favorites">
            <FavoritesTab />
          </TabsContent>
        </Tabs>

        {/* Order Detail Dialog */}
        <OrderDetailDialog
          orderId={viewOrderId}
          open={!!viewOrderId}
          onClose={() => setViewOrderId(null)}
          pricing={pricing}
        />
      </div>
    </div>
  );
};

export default ProfilePage;
